import { v4 as uuidv4 } from 'uuid';

/**
 * PSTUDIO-3610: same-browser cross-tab "nodes changed" notifications. A tab
 * broadcasts after every successful node persist; sibling tabs showing the
 * same solution reload via the existing XState `reloadNodes` invoke (see
 * use-cross-tab-node-sync.ts). Pure module — no store imports, so it stays
 * unit-testable and reusable from both the store (publish) and hooks
 * (subscribe).
 */
export const NODE_SYNC_CHANNEL_NAME = 'ps-solution-nodes';

/** Identifies THIS tab so a subscriber never reacts to its own broadcasts. */
export const NODE_SYNC_TAB_ID = uuidv4();

export interface NodeSyncMessage {
  solutionVersionId: string;
  sourceTabId: string;
}

function isBroadcastSupported(): boolean {
  return typeof BroadcastChannel !== 'undefined';
}

let publishChannel: BroadcastChannel | null = null;

/** Post "nodes of this solution changed" to sibling tabs. Fire-and-forget —
 *  an unavailable channel must never break the save path. */
export function broadcastNodesChanged(solutionVersionId: string): void {
  if (!isBroadcastSupported() || !solutionVersionId) return;
  try {
    if (!publishChannel) {
      publishChannel = new BroadcastChannel(NODE_SYNC_CHANNEL_NAME);
      // Node's BroadcastChannel (a global since Node 18 — i.e. in vitest
      // env=node) keeps the event loop alive unless unref'd, which would hang
      // `vitest run` at exit. Browsers have no unref — optional call is a no-op.
      (publishChannel as unknown as { unref?: () => void }).unref?.();
    }
    const message: NodeSyncMessage = {
      solutionVersionId,
      sourceTabId: NODE_SYNC_TAB_ID,
    };
    publishChannel.postMessage(message);
  } catch {
    publishChannel = null;
  }
}

/** Subscribe to sibling-tab node changes. Returns an unsubscribe fn. */
export function subscribeToNodesChanged(
  onMessage: (msg: NodeSyncMessage) => void
): () => void {
  if (!isBroadcastSupported()) return () => undefined;
  const channel = new BroadcastChannel(NODE_SYNC_CHANNEL_NAME);
  const handler = (event: MessageEvent) => {
    const msg = event.data as Partial<NodeSyncMessage> | null | undefined;
    if (!msg?.solutionVersionId || !msg.sourceTabId) return;
    if (msg.sourceTabId === NODE_SYNC_TAB_ID) return; // own broadcast
    onMessage(msg as NodeSyncMessage);
  };
  channel.addEventListener('message', handler);
  return () => {
    channel.removeEventListener('message', handler);
    channel.close();
  };
}
